/**
 * Dealer Groups Page (Master Admin)
 *
 * Two screens in one component:
 *  - LIST view: tabular list of dealer groups + Create button + Edit/Delete actions
 *  - DETAIL view: a selected group's rooftops (assign / remove), with a back button
 *
 * Master Admin only. Visible only via the Admin sidebar entry.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Plus, Building2, ChevronLeft, MoreHorizontal, Trash2, Pencil, Users } from "lucide-react";

const DealerGroupsPage = () => {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);   // group {id, name, description}
  const [deleting, setDeleting] = useState(null); // group
  const [saving, setSaving] = useState(false);

  // Drill-in
  const [activeGroup, setActiveGroup] = useState(null); // {id, name, description, rooftops:[]}
  const [activeLoading, setActiveLoading] = useState(false);

  // Assign rooftop modal
  const [showAssign, setShowAssign] = useState(false);
  const [rooftopSearch, setRooftopSearch] = useState("");
  const [availableRooftops, setAvailableRooftops] = useState([]); // dealers (Business)
  const [rooftopFetchLoading, setRooftopFetchLoading] = useState(false);
  const [rooftopOffset, setRooftopOffset] = useState(0);
  const [rooftopHasMore, setRooftopHasMore] = useState(false);
  const ROOFTOP_PAGE_SIZE = 50;
  const [assignTarget, setAssignTarget] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // rooftop ref
  const [removing, setRemoving] = useState(false);

  // ============== Loaders ==============
  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/admin/dealer-groups", { method: "GET" });
      setGroups(res.data?.groups || []);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to load dealer groups");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, toastErr]);

  useEffect(() => { loadGroups(); }, [loadGroups, tick]);

  const loadGroupDetail = useCallback(async (groupId) => {
    setActiveLoading(true);
    try {
      const res = await apiFetch(`/admin/dealer-groups/${groupId}`, { method: "GET" });
      setActiveGroup(res.data);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to load group detail");
      setActiveGroup(null);
    } finally {
      setActiveLoading(false);
    }
  }, [apiFetch, toastErr]);

  // ============== Create / Edit / Delete ==============
  const handleCreate = async (name, description) => {
    setSaving(true);
    try {
      await apiFetch("/admin/dealer-groups", {
        method: "POST",
        data: { name, description: description || null },
      });
      toastOk(`Group '${name}' created`);
      setShowCreate(false);
      setTick((t) => t + 1);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to create group");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id, name, description) => {
    setSaving(true);
    try {
      await apiFetch(`/admin/dealer-groups/${id}`, {
        method: "PUT",
        data: { name, description: description || null },
      });
      toastOk("Group updated");
      setEditing(null);
      setTick((t) => t + 1);
      if (activeGroup?.id === id) loadGroupDetail(id);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to update group");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await apiFetch(`/admin/dealer-groups/${deleting.id}`, { method: "DELETE" });
      toastOk(`Group '${deleting.name}' deleted`);
      setDeleting(null);
      setTick((t) => t + 1);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to delete group");
    } finally {
      setSaving(false);
    }
  };

  // ============== Rooftop assignment ==============
  // Server-side search + paginated fetch for the rooftop picker. Filters out
  // dealers already assigned to ANOTHER group (still allows current group's own
  // rooftops, in case the admin re-opens the picker mid-flow).
  const fetchAvailableRooftops = useCallback(async (search, offset, append) => {
    if (!activeGroup) return;
    setRooftopFetchLoading(true);
    try {
      const params = new URLSearchParams({
        include_archived: "false",
        limit: String(ROOFTOP_PAGE_SIZE),
        offset: String(offset),
      });
      if (search && search.trim()) params.set("search", search.trim());
      const res = await apiFetch(`/admin/dealers?${params.toString()}`, { method: "GET" });
      const dealers = res.data?.dealers || [];
      const filtered = dealers.filter(
        (d) => !d.dealer_group_id || d.dealer_group_id === activeGroup.id
      );
      setAvailableRooftops((prev) => (append ? [...prev, ...filtered] : filtered));
      setRooftopHasMore(dealers.length === ROOFTOP_PAGE_SIZE);
      setRooftopOffset(offset + dealers.length);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to load available rooftops");
    } finally {
      setRooftopFetchLoading(false);
    }
  }, [apiFetch, activeGroup, toastErr]);

  const openAssignModal = () => {
    setRooftopSearch("");
    setAssignTarget("");
    setAvailableRooftops([]);
    setRooftopOffset(0);
    setRooftopHasMore(false);
    setShowAssign(true);
    fetchAvailableRooftops("", 0, false);
  };

  // Debounce search input. fetchAvailableRooftops is memoized with
  // useCallback above and only changes when activeGroup/apiFetch/toastErr
  // do — including it in the deps array is now safe (audit Feb 27 2026)
  // and removes the previous eslint-disable opt-out.
  useEffect(() => {
    if (!showAssign) return;
    const t = setTimeout(() => {
      setAssignTarget("");
      fetchAvailableRooftops(rooftopSearch, 0, false);
    }, 250);
    return () => clearTimeout(t);
  }, [rooftopSearch, showAssign, fetchAvailableRooftops]);

  const handleAssign = async () => {
    if (!assignTarget || !activeGroup) return;
    setAssignSaving(true);
    try {
      await apiFetch(`/admin/dealer-groups/${activeGroup.id}/rooftops`, {
        method: "POST",
        data: { business_id: assignTarget },
      });
      toastOk("Rooftop assigned");
      setShowAssign(false);
      loadGroupDetail(activeGroup.id);
      setTick((t) => t + 1);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to assign rooftop");
    } finally {
      setAssignSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget || !activeGroup) return;
    setRemoving(true);
    try {
      await apiFetch(
        `/admin/dealer-groups/${activeGroup.id}/rooftops/${removeTarget.id}`,
        { method: "DELETE" }
      );
      toastOk(`'${removeTarget.name}' removed from group`);
      setRemoveTarget(null);
      loadGroupDetail(activeGroup.id);
      setTick((t) => t + 1);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to remove rooftop");
    } finally {
      setRemoving(false);
    }
  };

  // ============== Render: detail view ==============
  if (activeGroup) {
    return (
      <div className="space-y-6" data-testid="dealer-group-detail">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => setActiveGroup(null)}
            data-testid="dealer-group-back-btn"
          >
            <ChevronLeft size={16} className="mr-1" /> Back to Groups
          </Button>
        </div>

        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900" data-testid="active-group-name">
              {activeGroup.name}
            </h2>
            {activeGroup.description && (
              <p className="text-sm text-gray-600 mt-1">{activeGroup.description}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {activeGroup.rooftops?.length || 0} rooftop(s)
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEditing(activeGroup)}
              data-testid="edit-group-btn"
            >
              <Pencil size={14} className="mr-1.5" /> Edit
            </Button>
            <Button
              onClick={openAssignModal}
              data-testid="add-rooftop-btn"
            >
              <Plus size={14} className="mr-1.5" /> Add Rooftop
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Rooftops in this Group</CardTitle>
            <CardDescription>
              Each rooftop is a dealership location. Users granted access to this group&apos;s rooftops can switch between them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeLoading ? (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : (activeGroup.rooftops?.length || 0) === 0 ? (
              <div className="text-center py-10 text-gray-500" data-testid="no-rooftops">
                <Building2 size={28} className="mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No rooftops yet. Click &quot;Add Rooftop&quot; to get started.</p>
              </div>
            ) : (
              <table className="data-table" data-testid="rooftops-table">
                <thead>
                  <tr>
                    <th>Rooftop Name</th>
                    <th>Franchise</th>
                    <th>Status</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {activeGroup.rooftops.map((r) => (
                    <tr key={r.id} data-testid={`rooftop-row-${r.id}`}>
                      <td className="font-medium">{r.name}</td>
                      <td>
                        {r.franchise_make ? (
                          <Badge variant="secondary">{r.franchise_make}</Badge>
                        ) : (
                          <span className="text-gray-400 text-xs italic">Not set</span>
                        )}
                      </td>
                      <td>
                        {r.archived ? (
                          <Badge variant="outline" className="text-gray-500">Archived</Badge>
                        ) : r.is_active ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                        ) : (
                          <Badge variant="outline">Disabled</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRemoveTarget(r)}
                          data-testid={`remove-rooftop-${r.id}`}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Edit modal (re-used from list) */}
        <GroupFormDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          group={editing}
          onSubmit={(name, description) => handleEdit(editing.id, name, description)}
          saving={saving}
        />

        {/* Assign rooftop modal */}
        <Dialog open={showAssign} onOpenChange={setShowAssign}>
          <DialogContent data-testid="assign-rooftop-dialog">
            <DialogHeader>
              <DialogTitle>Add Rooftop</DialogTitle>
              <DialogDescription>
                Pick a dealer to add as a rooftop in <strong>{activeGroup.name}</strong>. Dealers
                already in another group are filtered out.
              </DialogDescription>
            </DialogHeader>

            <div className="py-2 space-y-3">
              <Input
                value={rooftopSearch}
                onChange={(e) => setRooftopSearch(e.target.value)}
                placeholder="Search dealers by name…"
                autoFocus
                data-testid="rooftop-search-input"
              />

              <div
                className="max-h-72 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100"
                data-testid="rooftop-picker-list"
              >
                {rooftopFetchLoading && availableRooftops.length === 0 ? (
                  <div className="p-3 space-y-2">
                    {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                  </div>
                ) : availableRooftops.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500" data-testid="no-rooftops-found">
                    {rooftopSearch.trim()
                      ? `No matching dealers for "${rooftopSearch.trim()}".`
                      : "No available dealers. All dealers are either archived or already in another group."}
                  </div>
                ) : (
                  availableRooftops.map((d) => {
                    const isSelected = assignTarget === d.id;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setAssignTarget(d.id)}
                        className={`w-full text-left px-3 py-2.5 transition-colors ${
                          isSelected ? "bg-purple-50" : "hover:bg-gray-50"
                        }`}
                        data-testid={`rooftop-picker-row-${d.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{d.name}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {d.franchise_make ? `${d.franchise_make} · ` : ""}
                              {d.public_key ? `Key: ${d.public_key.slice(0, 12)}…` : ""}
                            </div>
                          </div>
                          {isSelected && (
                            <span className="text-xs font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                              Selected
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}

                {rooftopHasMore && (
                  <button
                    type="button"
                    onClick={() => fetchAvailableRooftops(rooftopSearch, rooftopOffset, true)}
                    disabled={rooftopFetchLoading}
                    className="w-full text-center px-3 py-2.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
                    data-testid="rooftop-picker-load-more"
                  >
                    {rooftopFetchLoading ? "Loading…" : "Load more"}
                  </button>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
              <Button
                onClick={handleAssign}
                disabled={!assignTarget || assignSaving}
                data-testid="confirm-assign-rooftop-btn"
              >
                {assignSaving ? "Assigning…" : "Add to Group"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove rooftop confirm */}
        <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
          <DialogContent data-testid="remove-rooftop-dialog">
            <DialogHeader>
              <DialogTitle>Remove rooftop?</DialogTitle>
              <DialogDescription>
                <strong>{removeTarget?.name}</strong> will be removed from <strong>{activeGroup.name}</strong>.
                Any user rooftop access to this rooftop will be revoked. The dealer itself is NOT deleted.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoveTarget(null)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleRemove}
                disabled={removing}
                data-testid="confirm-remove-rooftop-btn"
              >
                {removing ? "Removing…" : "Remove from Group"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============== Render: list view ==============
  return (
    <div className="space-y-6" data-testid="dealer-groups-page">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="page-title text-xl font-semibold text-gray-900">Dealer Groups</h2>
          <p className="text-sm text-gray-600 mt-1">
            Auto groups that own multiple dealership rooftops. A user with rooftop access can switch between locations from the top nav.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-group-btn">
          <Plus size={14} className="mr-1.5" /> New Group
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 px-4" data-testid="no-groups-empty">
              <Users size={32} className="mx-auto mb-2 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">No dealer groups yet</p>
              <p className="text-xs text-gray-500 mt-1">
                Create a group to consolidate multiple rooftops under one auto group.
              </p>
              <Button className="mt-4" onClick={() => setShowCreate(true)} data-testid="empty-create-group-btn">
                <Plus size={14} className="mr-1.5" /> Create your first group
              </Button>
            </div>
          ) : (
            <table className="data-table" data-testid="groups-table">
              <thead>
                <tr>
                  <th>Group Name</th>
                  <th>Rooftops</th>
                  <th>Created</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr
                    key={g.id}
                    data-testid={`group-row-${g.id}`}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => loadGroupDetail(g.id)}
                  >
                    <td>
                      <div className="font-medium text-gray-900">{g.name}</div>
                      {g.description && (
                        <div className="text-xs text-gray-500">{g.description}</div>
                      )}
                    </td>
                    <td>
                      <Badge variant="secondary" data-testid={`group-rooftop-count-${g.id}`}>
                        {g.rooftop_count}
                      </Badge>
                    </td>
                    <td className="text-sm text-gray-600">
                      {g.created_at ? new Date(g.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`group-actions-${g.id}`}>
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => loadGroupDetail(g.id)} data-testid={`group-view-${g.id}`}>
                            <Building2 size={14} className="mr-2" /> View Rooftops
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditing(g)} data-testid={`group-edit-${g.id}`}>
                            <Pencil size={14} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleting(g)}
                            className="text-red-600"
                            data-testid={`group-delete-${g.id}`}
                          >
                            <Trash2 size={14} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create */}
      <GroupFormDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        group={null}
        onSubmit={handleCreate}
        saving={saving}
      />

      {/* Edit */}
      <GroupFormDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        group={editing}
        onSubmit={(name, description) => handleEdit(editing.id, name, description)}
        saving={saving}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent data-testid="delete-group-dialog">
          <DialogHeader>
            <DialogTitle>Delete Dealer Group?</DialogTitle>
            <DialogDescription>
              This permanently deletes <strong>{deleting?.name}</strong>. The group must have zero rooftops first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
              data-testid="confirm-delete-group-btn"
            >
              {saving ? "Deleting…" : "Delete Group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== Group form dialog (create/edit) ==============
const GroupFormDialog = ({ open, onClose, group, onSubmit, saving }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (open) {
      setName(group?.name || "");
      setDescription(group?.description || "");
    }
  }, [open, group]);

  const isEdit = !!group;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="group-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Dealer Group" : "New Dealer Group"}</DialogTitle>
          <DialogDescription>
            Auto groups own multiple dealership rooftops. Users granted access to this group&apos;s rooftops can switch between them from the top nav.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Group Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Crossroads Auto Group"
              maxLength={255}
              data-testid="group-form-name"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Description (optional)
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Internal notes"
              data-testid="group-form-description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSubmit(name.trim(), description.trim())}
            disabled={!name.trim() || saving}
            data-testid="group-form-submit"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DealerGroupsPage;
